import type { Profile } from "../profile.ts"
import type { Route } from "./+types/home.ts"

import * as UI from "@iyk/ui"
import * as React from "react"

import { Res } from "@iyk/vf-server"
import { Array, Effect, Order } from "effect"
import { appConfig } from "../../../app-config.ts"
import { Handler } from "../../../server/handler.ts"
import { AlertCard } from "../ui/alert-card.tsx"
import { ExclusiveCardList } from "../ui/exclusive-card-list.tsx"
import { ExclusiveEmptyCard } from "../ui/exclusive-empty-card.tsx"
import { HowToTapDialog } from "../ui/how-to-tap-dialog.tsx"
import { Link, Routes } from "../ui/link.tsx"
import { MerchCard } from "../ui/merch-card.tsx"
import { ShowScheduleLink } from "../ui/show-schedule-item.tsx"
import { UserProfileCard } from "../ui/user-profile-card.tsx"

type LoaderData = {
  nextShow?: Profile.Show
  merchSkus: Array<Profile.Merch>
  exclusives: Array<Profile.Exclusive>
  totalShows: number
}

export const loader = Handler.make<LoaderData>("profile-home", ({ api, sessions }) => {
  return Effect.all(
    {
      nextShow: api.findNextShow,
      merchSkus: api.listMerch,
      totalShows: api.listShows.pipe(Effect.map(Array.length)),
      exclusives: sessions.authenticated.pipe(
        Effect.flatMap((session) => api.findUnlockedExclusives(session.user.id)),
        Effect.orElseSucceed(() => [])
      )
    },
    { concurrency: "unbounded" }
  ).pipe(Effect.map(Res.json))
})

export default function ProfileRoute(props: Route.ComponentProps) {
  const layoutData = props.matches[1].data
  const sortedSkus = Array.sortWith(
    props.loaderData.merchSkus,
    (sku) => (layoutData.user?.ownedMerchSkuIds.includes(sku.id) ? 0 : 1),
    Order.number
  )

  return layoutData.user ? (
    <LoggedInProfile
      user={layoutData.user}
      exclusives={props.loaderData.exclusives}
      nextShow={props.loaderData.nextShow}
      totalShows={props.loaderData.totalShows}
      merchSkus={sortedSkus}
    />
  ) : (
    <LoggedOutProfile nextShow={props.loaderData.nextShow} totalShows={props.loaderData.totalShows} />
  )
}

const LoggedInProfile = ({
  user,
  nextShow,
  totalShows,
  alert,
  merchSkus,
  exclusives
}: LoggedInProfile.Props) => {
  const [openHowToTapDialog, setOpenHowToTapDialog] = React.useState(false)
  const hasUnlockedItems = user.ownedMerchSkuIds.length > 0

  return (
    <div className="flex flex-col gap-8 px-4">
      <UserProfileCard
        userId={user.id}
        username={user.username}
        since={user.memberSince}
        totalMerchOwned={user.ownedMerchSkuIds.length}
        totalCheckIns={user.attendedShowIds.length}
        variant="profile"
      />
      {alert && (
        <AlertCard
          eyebrow={alert.eyebrow}
          title={alert.title}
          subtitle={alert.subtitle}
          announcementTitle={alert.announcementTitle}
          announcementDate={alert.announcementDate}
          description={alert.description}
          kind={alert.kind}
        />
      )}
      {/* {totalShows > 0 && <UploadFanClipsCard />} */}
      <section className="flex flex-col gap-4">
        <h2 className="text-base">Your Unlocks</h2>
        <div className="grid gap-2">
          {Array.isEmptyArray(exclusives) ? (
            <ExclusiveEmptyCard />
          ) : (
            <ExclusiveCardList owned={true} exclusives={exclusives} />
          )}
        </div>
      </section>
      <section className="flex flex-col gap-4">
        <h2 className="text-base">Items</h2>
        {!hasUnlockedItems && appConfig.assets.videos.howToTap && (
          <HowToTapDialog
            videoSrc={appConfig.assets.videos.howToTap}
            open={openHowToTapDialog}
            setOpen={setOpenHowToTapDialog}
          />
        )}
        <div className="grid grid-cols-2 gap-0.5">
          {merchSkus.map((merchSku) => (
            <MerchCard
              key={merchSku.id}
              merchId={merchSku.id}
              title={merchSku.title}
              imageSrc={merchSku.imageSrc}
              owned={user.ownedMerchSkuIds.includes(merchSku.id)}
            />
          ))}
        </div>
      </section>
      {nextShow && <NextShow nextShow={nextShow} showCount={totalShows} />}
    </div>
  )
}

namespace LoggedInProfile {
  export interface Props {
    user: Profile.User
    merchSkus: Array<Profile.Merch>
    exclusives: Array<Profile.Exclusive>
    totalShows: number
    nextShow?: Profile.Show
    alert?: AlertCard.Props
  }
}

// TODO: add login button and auth sheet
const LoggedOutProfile = ({ nextShow, totalShows }: LoggedOutProfile.Props) => {
  const [openHowToTapDialog, setOpenHowToTapDialog] = React.useState(false)

  return (
    <div className="flex flex-col gap-8 px-4">
      <section className="grid place-items-center gap-8 px-6 text-center mt-6">
        <h2 className="uppercase text-xs-departure">
          Portman road
          <br />
          commemorative lanyard
        </h2>
        {appConfig.assets.images.howToTap && (
          <img
            className="aspect-square w-full cursor-pointer"
            onClick={() => setOpenHowToTapDialog(true)}
            src={appConfig.assets.images.howToTap}
            alt="Portman road commemorative lanyard"
          />
        )}
        <div className="grid place-items-center gap-4">
          <p className="text-sm">
            Tap the lanyard to unlock bonus content and
            <br /> access to ‘Play’
          </p>
          {appConfig.assets.videos.howToTap && (
            <HowToTapDialog
              videoSrc={appConfig.assets.videos.howToTap}
              trigger={
                <UI.Button type="button" kind="solid" size="sm" className="w-fit">
                  How to tap
                </UI.Button>
              }
              open={openHowToTapDialog}
              setOpen={setOpenHowToTapDialog}
            />
          )}
        </div>
      </section>

      {nextShow && (
        <section className="flex flex-col gap-4">
          <div className="flex justify-between items-center">
            <h2 className="text-base">Next show</h2>
            <Link to={Routes.tourSchedule} className={UI.classForLink({ className: "text-sm" })}>
              View all ({totalShows})
            </Link>
          </div>
          {nextShow && (
            <ShowScheduleLink
              id={nextShow.id}
              city={nextShow.city}
              state={nextShow.state}
              dayOfMonth={nextShow.dayOfMonth}
              dayOfWeek={nextShow.dayOfWeek}
              time={nextShow.time}
              venue={nextShow.venue}
              url={nextShow.url}
              month={nextShow.month}
              checked={false}
            />
          )}
        </section>
      )}
    </div>
  )
}

namespace LoggedOutProfile {
  export interface Props {
    nextShow?: Profile.Show
    totalShows: number
  }
}

const NextShow = ({ nextShow, showCount }: NextShow.Props) => (
  <section className="flex flex-col gap-4">
    <div className="flex justify-between items-center">
      <h2 className="text-base">Next show</h2>
      <Link to={Routes.tourSchedule} className={UI.classForLink({ className: "text-sm" })}>
        View all ({showCount})
      </Link>
    </div>
    {nextShow && (
      <ShowScheduleLink
        id={nextShow.id}
        city={nextShow.city}
        state={nextShow.state}
        dayOfMonth={nextShow.dayOfMonth}
        dayOfWeek={nextShow.dayOfWeek}
        time={nextShow.time}
        venue={nextShow.venue}
        url={nextShow.url}
        month={nextShow.month}
        checked={false}
      />
    )}
  </section>
)

namespace NextShow {
  export interface Props {
    nextShow?: Profile.Show
    showCount: number
  }
}

const CheckInCard = ({ title, description }: CheckInCard.Props) => (
  <section className="flex flex-col gap-4 p-4 bshadow bshadow-gray-5">
    <div className="grid gap-1">
      <h3 className="text-sm text-gray-12">{title}</h3>
      <p className="text-sm text-gray-11">{description}</p>
    </div>
    <Link
      to={Routes.checkIn}
      className={UI.classForButton({ size: "sm", kind: "solid", className: "w-max" })}
    >
      Check in
    </Link>
  </section>
)

// @ts-ignore reason: this is not used yet, otherwise we get a typecheck error
const UploadFanClipsCard = () => (
  <section className="flex flex-col gap-4 p-4 bshadow bshadow-gray-5">
    <div className="grid gap-1">
      <h3 className="text-sm text-gray-12">Upload your content</h3>
      <p className="text-sm text-gray-11">
        Share your experience for a chance to be featured on the official {appConfig.artist.name} Instagram
      </p>
    </div>
    <Link
      to={Routes.fanClipsNew}
      className={UI.classForButton({ size: "sm", kind: "solid", className: "w-max" })}
    >
      Upload
    </Link>
  </section>
)

namespace CheckInCard {
  export interface Props {
    title: string
    description: string
  }
}
